<script lang="ts">
  import { tick } from 'svelte';
  import type { Store } from '../state/store.svelte';
  import { selectLegendRows, type BodyView } from '../state/selectors';
  import type { BodyId } from '../state/selectors';

  // Body Legend (left-docked). Story 1.3: each row is a genuine accessible
  // selection control (a button with click + native Enter/Space) that dispatches
  // SelectBody. Story 3.1 adds reorder: a per-row Move Up / Move Down pair (the
  // SC 2.5.7 single-pointer AND keyboard alternative) plus a pointer drag handle,
  // both raising the identical `ReorderLegend` command carrying the full resulting
  // order (AD-2). Reordering changes ONLY presentation order; it never selects a
  // body or touches orbital state. The selection control and the reorder controls
  // are SIBLINGS (a <button> cannot nest buttons). Colour is never the sole
  // identifier: every row pairs its identity-colour marker with the body name and,
  // for state, a text chip; the move labels name the body and direction.
  let {
    store,
    announce
  }: { store: Store; announce?: (message: string) => void } = $props();

  const rows = $derived(selectLegendRows(store.state));

  // A native <button> gets Enter/Space activation for free (AD-8). `body` is a
  // BodyId sourced from the selector view, so no `core` import is needed in ui.
  function select(id: BodyView['id']): void {
    store.dispatch({ type: 'SelectBody', body: id });
  }

  function nameOf(id: BodyId): string {
    return rows.find((r) => r.id === id)?.name ?? id;
  }

  // Both entry points (buttons + drag) dispatch the SAME ReorderLegend command and
  // announce the SAME wording, so they cannot diverge (AD-2 identical-outcome).
  function dispatchOrder(order: BodyId[], movedId: BodyId, newIndex: number): void {
    store.dispatch({ type: 'ReorderLegend', order });
    announce?.(`${nameOf(movedId)} moved to position ${newIndex + 1} of ${order.length}.`);
  }

  // Move Up / Move Down: the single-pointer no-drag + keyboard path (SC 2.5.7).
  // Build the full new order from the CURRENT order by swapping with the neighbour.
  // Focus retention (AC-3/AC-4): after the keyed reorder moves DOM nodes, refocus
  // the SAME control on the moved body; if it is now disabled because the body hit
  // a boundary, focus its still-enabled sibling so focus never falls to <body>.
  async function move(id: BodyId, direction: 'up' | 'down'): Promise<void> {
    const order = [...store.state.legendOrder];
    const i = order.indexOf(id);
    const j = direction === 'up' ? i - 1 : i + 1;
    if (i < 0 || j < 0 || j >= order.length) return; // boundary no-op (also disabled in UI)
    [order[i], order[j]] = [order[j], order[i]];
    dispatchOrder(order, id, j);
    await tick();
    refocusMovedControl(id, direction);
  }

  function refocusMovedControl(id: BodyId, direction: 'up' | 'down'): void {
    const up = document.querySelector<HTMLButtonElement>(`[data-testid="legend-move-up-${id}"]`);
    const down = document.querySelector<HTMLButtonElement>(
      `[data-testid="legend-move-down-${id}"]`
    );
    const invoked = direction === 'up' ? up : down;
    const sibling = direction === 'up' ? down : up;
    if (invoked && !invoked.disabled) invoked.focus();
    else if (sibling && !sibling.disabled) sibling.focus();
  }

  // --- Pointer drag-reorder (the pointer-native path, AC-2) ---------------------
  // A pointer-events drag (deterministic to drive from Playwright, unlike native
  // HTML5 drag-and-drop). The gesture starts on a dedicated drag handle inside the
  // reorder controls, never on the selection button, so it cannot fire SelectBody.
  let listEl: HTMLUListElement | undefined;
  let draggingId = $state<BodyId | null>(null);

  function itemIndexAtY(clientY: number): number {
    if (!listEl) return 0;
    const items = Array.from(listEl.querySelectorAll<HTMLElement>('[data-legend-item]'));
    for (let i = 0; i < items.length; i++) {
      const r = items[i].getBoundingClientRect();
      // Above this row's midpoint -> insert BEFORE it (top-half of row i, incl. the
      // first row -> index 0). Below every midpoint falls through to the append case.
      if (clientY < r.top + r.height / 2) return i;
    }
    // Past the LAST row's midpoint: the append position (items.length), so a drop in
    // the bottom half of the last row can land the dragged body as the final element.
    // The insert logic's `target > from ? target - 1 : target` maps this to the true
    // final slot after the dragged row is removed.
    return items.length;
  }

  function onHandlePointerDown(event: PointerEvent, id: BodyId): void {
    // Left button / primary pointer only; do not start a drag on selection.
    if (event.button !== 0) return;
    event.preventDefault();
    draggingId = id;
    const handle = event.currentTarget as HTMLElement;
    handle.setPointerCapture?.(event.pointerId);
  }

  function onHandlePointerUp(event: PointerEvent): void {
    const id = draggingId;
    if (id === null) return;
    const handle = event.currentTarget as HTMLElement;
    handle.releasePointerCapture?.(event.pointerId);
    const target = itemIndexAtY(event.clientY);
    const order = [...store.state.legendOrder];
    const from = order.indexOf(id);
    draggingId = null;
    if (from === -1) return; // dragged id not in order: nothing to do
    // `target` is an index into the PRE-removal array. Removing the dragged row
    // shifts every later row up by one, so for a downward drag (target > from) the
    // insertion index in the post-removal array is target - 1; upward drags are
    // unshifted. Announce the REAL landing row (order.indexOf(id)), not `target`.
    const insertAt = target > from ? target - 1 : target;
    // No-op drop: any release resolving to the dragged row's own slot reproduces
    // the identical order. This covers the exact-position case (target === from)
    // AND the dead zone one slot below (target === from + 1 -> insertAt === from).
    // Return BEFORE splicing/dispatching so no ReorderLegend and no live-region
    // announcement fire for a move that did not happen (SC 4.1.3), mirroring the
    // keyboard path's "only announce on an actual move" rule.
    if (insertAt === from) return;
    order.splice(from, 1);
    order.splice(insertAt, 0, id);
    dispatchOrder(order, id, order.indexOf(id));
  }
</script>

<nav class="legend glass-panel" aria-label="Body legend" data-testid="legend">
  <h2 class="legend-title">Bodies</h2>
  <ul class="legend-list" bind:this={listEl}>
    {#each rows as row, index (row.id)}
      <li class="legend-item" data-legend-item class:dragging={draggingId === row.id}>
        <!-- Interactive selection control (Story 1.3): click or Enter/Space
             selects the body. aria-pressed reflects the current selection so the
             state is not colour-only. -->
        <button
          type="button"
          class="legend-row"
          class:selected={row.isSelected}
          class:removed={row.status !== 'active'}
          aria-pressed={row.isSelected}
          data-testid={`legend-row-${row.id}`}
          onclick={() => select(row.id)}
          aria-label={`${row.name}${row.isCentral ? ', central body' : ''}${
            row.isSelected ? ', selected body' : ''
          }, status ${row.status}`}
        >
          <span
            class="marker"
            style={`--marker-colour: ${row.colour}`}
            aria-hidden="true"
          ></span>
          <span class="name">{row.name}</span>
          <!-- Central-body cue: an intuitive target/focus GLYPH rather than a text
               chip (keeps the row compact so wide state chips no longer collide with
               the reorder controls). Not colour-only: it is a distinct SHAPE, and the
               row's aria-label still announces ", central body" for assistive tech. -->
          {#if row.isCentral}
            <span
              class="centre-icon material-symbols-outlined"
              data-testid={`chip-centre-${row.id}`}
              title="Central body"
              aria-hidden="true">adjust</span
            >
          {/if}
          <!-- Selection needs NO text: the whole row is filled and carries a left
               accent bar (see .selected styles). aria-pressed + the aria-label convey
               it to assistive tech, so this stays perceivable without colour alone. -->
          <!-- Story 4.1 (AC 5): a distinct COLLIDED chip for a removed-by-collision
               row; the row stays present, reorderable, and inspectable. -->
          {#if row.status === 'collided'}
            <span
              class="chip chip-collided label-caps"
              data-testid={`chip-collided-${row.id}`}>Collided</span
            >
          {/if}
          <!-- Story 4.2 (AC 4): a distinct ABSORBED chip (separate from COLLIDED so
               the learner tells central absorption from a body-body collision) for a
               body removed by the centre; the row stays present and inspectable. -->
          {#if row.status === 'absorbed'}
            <span
              class="chip chip-absorbed label-caps"
              data-testid={`chip-absorbed-${row.id}`}>Absorbed</span
            >
          {/if}
        </button>
        <!-- Reorder controls: SIBLINGS of the selection button (never nested).
             Move Up / Move Down are the accessible keyboard + single-pointer path
             (SC 2.5.7); the drag handle is the pointer-native path (AC-2). -->
        <div class="reorder-controls">
          <span
            class="drag-handle material-symbols-outlined"
            data-testid={`legend-drag-${row.id}`}
            aria-hidden="true"
            onpointerdown={(e) => onHandlePointerDown(e, row.id)}
            onpointerup={onHandlePointerUp}
          >
            drag_indicator
          </span>
          <button
            type="button"
            class="reorder-btn"
            data-testid={`legend-move-up-${row.id}`}
            disabled={index === 0}
            aria-label={`Move ${row.name} up`}
            onclick={() => move(row.id, 'up')}
          >
            <span class="material-symbols-outlined" aria-hidden="true">arrow_upward</span>
          </button>
          <button
            type="button"
            class="reorder-btn"
            data-testid={`legend-move-down-${row.id}`}
            disabled={index === rows.length - 1}
            aria-label={`Move ${row.name} down`}
            onclick={() => move(row.id, 'down')}
          >
            <span class="material-symbols-outlined" aria-hidden="true">arrow_downward</span>
          </button>
        </div>
      </li>
    {/each}
  </ul>
</nav>

<style>
  .legend {
    position: absolute;
    top: calc(var(--top-bar-height) + 68px);
    left: var(--edge-margin);
    width: var(--legend-width);
    max-height: calc(100vh - var(--top-bar-height) - 68px - var(--edge-margin));
    overflow-y: auto;
    padding: var(--panel-padding);
    z-index: 8;
  }

  .legend-title {
    margin: 0 0 12px;
    font-size: 14px;
    font-weight: 600;
    color: var(--on-surface-variant);
  }

  .legend-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 4px;
    border-radius: var(--radius-default);
  }

  /* Drag state cue by shape/opacity, not colour alone (AD-8); preserved under
     reduced motion (no transition removes the cue). */
  .legend-item.dragging {
    outline: 2px dashed #d4e3ff;
    outline-offset: 1px;
    opacity: 0.85;
  }

  .legend-row {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border: none;
    border-radius: var(--radius-default);
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .legend-row:hover {
    background: rgba(255, 255, 255, 0.06);
  }

  /* Generic focus ring (DESIGN components.focus-ring): visible for keyboard users. */
  .legend-row:focus-visible {
    outline: 2px solid #d4e3ff;
    outline-offset: 2px;
  }

  /* Selection is shown WITHOUT text: a filled row plus a left accent bar. The bar is
     a non-colour SHAPE cue (its presence, not its hue, marks the row), and the fill
     is a lightness change; aria-pressed conveys it to assistive tech (AD-8, not
     colour-only). Mirrors the canvas selection treatment so the two read as one. */
  .legend-row.selected {
    background: var(--primary-container);
    color: var(--on-primary-container);
  }

  .legend-row.selected::before {
    content: '';
    position: absolute;
    left: 3px;
    top: 7px;
    bottom: 7px;
    width: 3px;
    border-radius: var(--radius-full);
    background: var(--primary);
  }

  /* Selected marker echoes the on-canvas selection ring: a thin ring around the
     identity dot, reinforcing the row fill without any text. */
  .legend-row.selected .marker {
    box-shadow:
      0 0 0 2px var(--on-primary-container),
      0 0 6px var(--marker-colour);
  }

  .reorder-controls {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 2px;
  }

  .drag-handle {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 28px;
    font-size: 18px;
    color: var(--on-surface-variant);
    cursor: grab;
    touch-action: none;
    user-select: none;
  }

  .drag-handle:active {
    cursor: grabbing;
  }

  .reorder-btn {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    border-radius: var(--radius-default);
    background: transparent;
    color: var(--on-surface-variant);
    cursor: pointer;
  }

  .reorder-btn .material-symbols-outlined {
    font-size: 18px;
  }

  .reorder-btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.06);
    color: var(--on-surface);
  }

  .reorder-btn:focus-visible {
    outline: 2px solid #d4e3ff;
    outline-offset: 2px;
  }

  .reorder-btn:disabled {
    opacity: 0.32;
    cursor: default;
  }

  .marker {
    flex: 0 0 auto;
    width: 14px;
    height: 14px;
    border-radius: var(--radius-full);
    background: var(--marker-colour);
    box-shadow: 0 0 6px var(--marker-colour);
  }

  .name {
    flex: 1 1 auto;
    font-size: 14px;
  }

  .chip {
    flex: 0 0 auto;
    padding: 3px 8px;
    border-radius: var(--radius-full);
  }

  /* Central-body glyph: a compact target/focus icon in the accent colour. Shape is
     the cue (not colour alone); the row aria-label still says ", central body". */
  .centre-icon {
    flex: 0 0 auto;
    font-size: 18px;
    line-height: 1;
    color: var(--primary);
  }

  .legend-row.selected .centre-icon {
    color: var(--on-primary-container);
  }

  /* Removed-by-collision chip (Story 4.1). Not colour-alone: the row also carries
     the strikethrough name treatment below and the "status collided" aria-label. */
  .chip-collided {
    background: rgba(255, 138, 128, 0.18);
    color: #ff8a80;
  }

  /* Removed-by-absorption chip (Story 4.2). Reserved error family, visually distinct
     from the collided chip. Not colour-alone: the row also carries the strikethrough
     name treatment and the "status absorbed" aria-label. */
  .chip-absorbed {
    background: rgba(255, 84, 73, 0.2);
    color: #ff5449;
  }

  /* Removed-row treatment: strikethrough + reduced opacity so the state reads
     without relying on colour alone (AD-8). The row stays selectable/inspectable. */
  .legend-row.removed .name {
    text-decoration: line-through;
    opacity: 0.6;
  }

  .legend-row.removed .marker {
    opacity: 0.45;
  }
</style>
